/**
 * @file 汎用SortableContainerコンポーネント
 *
 * dnd-kit公式ドキュメントの推奨パターンに従った実装：
 * - DragOverlay内ではuseSortableを使用しないコンポーネントをレンダリング
 * - snapCenterToCursorで座標を安定化
 * - pointerWithinで正確なドロップ判定
 */

"use client";

import {
  DndContext,
  type DragEndEvent,
  type DragStartEvent,
  pointerWithin,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { type ReactNode } from "react";
import { cn } from "@/lib/utils";
import { DragOverlayPortal } from "./drag-overlay-portal";

interface SortableContainerProps<T extends { id: string }> {
  items: T[];
  renderItem: (item: T, isDragging: boolean, isUpdating: boolean) => ReactNode;
  renderDragOverlay?: (item: T) => ReactNode;
  onDragStart?: (event: DragStartEvent) => void;
  onDragEnd: (event: DragEndEvent) => void;
  onDragCancel?: () => void;
  activeId: string | null;
  isUpdating?: boolean;
  sensors: ReturnType<typeof useSensors>;
  className?: string;
  dndContextId?: string;
}

/**
 * 汎用的なSortableコンテナコンポーネント
 * DnDの基本的な機能を提供し、アイテムのレンダリングは外部から注入
 */
export function SortableContainer<T extends { id: string }>({
  items,
  renderItem,
  renderDragOverlay,
  onDragStart,
  onDragEnd,
  onDragCancel,
  activeId,
  isUpdating = false,
  sensors,
  className,
  dndContextId,
}: SortableContainerProps<T>) {
  const isDragging = activeId !== null;
  const activeItem = items.find((item) => item.id === activeId);

  if (items.length === 0) {
    return null;
  }

  return (
    <div className={cn("relative", className)}>
      <DndContext
        id={dndContextId}
        sensors={sensors}
        collisionDetection={pointerWithin}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onDragCancel={onDragCancel}
      >
        <SortableContext
          items={items.map((item) => item.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className={cn("flex flex-col", isDragging && "cursor-grabbing")}>
            {items.map((item) => (
              <div key={item.id}>
                {renderItem(item, isDragging, isUpdating)}
              </div>
            ))}
          </div>
        </SortableContext>

        {/* DragOverlayをポータル経由でbody直下にレンダリング */}
        <DragOverlayPortal activeId={activeId}>
          {activeItem && renderDragOverlay && renderDragOverlay(activeItem)}
        </DragOverlayPortal>
      </DndContext>
    </div>
  );
}
